import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import { getProfileImage } from "@shared/hooks/useProfile";
import React from "react";
import { CreateGroupModalProps } from "../types";

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  open,
  onClose,
  groupTitle,
  setGroupTitle,
  selectedUsers,
  setSelectedUsers,
  userProfiles,
  onSubmit,
  color,
  setColor,
  editMode = false,
  canEdit = true,
}) => {
  const handleToggleUser = (userId: string) => {
    setSelectedUsers(
      selectedUsers.includes(userId)
        ? selectedUsers.filter((id) => id !== userId)
        : [...selectedUsers, userId],
    );
  };

  return (
    <ModalBlank
      id="create-group-modal"
      className="max-h-[80vh] overflow-y-auto p-4"
      modalOpen={open}
      setModalOpen={onClose}
      showCloseButton={true}
    >
      <form className="space-y-4 p-2" onSubmit={onSubmit}>
        <h2 className="text-lg font-semibold mb-2">
          {editMode ? "Edit group" : "Create new group"}
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Group name</label>
            <input
              type="text"
              className="form-input w-full"
              placeholder="Group name"
              value={groupTitle}
              onChange={(e) => setGroupTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setColor(e.target.value)
                }
                className="w-8 h-8 p-0 border-0 bg-transparent cursor-pointer"
                title="Pick group color"
              />
              <span
                className="inline-block w-6 h-6 rounded-full border border-gray-300"
                style={{ backgroundColor: color }}
              />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">
            Select friends
          </label>
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
            {Object.values(userProfiles).map((user) => (
              <label
                key={user.id}
                className="flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => handleToggleUser(user.id)}
                  className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-blue-500 accent-blue-500 cursor-pointer"
                />
                <img
                  src={getProfileImage(user)}
                  className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-700 shrink-0"
                  alt={user.name}
                />
                <span className="text-sm text-gray-800 dark:text-gray-100">
                  {user.name}
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={editMode && !canEdit}
          >
            {editMode ? "Save changes" : "Create group"}
          </Button>
        </div>
      </form>
      {editMode && !canEdit && (
        <div className="text-red-500 text-xs mt-2">
          Only the creator can edit this group.
        </div>
      )}
    </ModalBlank>
  );
};

export default CreateGroupModal;
